/**
 * useSubscriptionStatus — fetches GET /api/v1/subscriptions/status once
 * per session and caches in a module-level Promise to dedupe concurrent
 * calls. Drives both the Slice 5 navbar countdown pill AND the
 * ExpiredGuard read-only lockdown wrapper.
 *
 * Exports:
 *   useSubscriptionStatus() — { status, isLoading, error, refetch }
 *
 * `status` shape (subset relevant to Slice 5; full payload from BE):
 *   {
 *     is_trial: bool,
 *     trial_end_date: "YYYY-MM-DD" | null,
 *     trial_expired_at: ISO datetime | null,
 *     days_remaining: number,
 *     days_left: number,           // clamped to >= 0
 *     subscription_state: "trial" | "trial_expired" | "subscribed" | "expired",
 *     subscription_plan: string | null,
 *     subscription_end_date: "YYYY-MM-DD" | null,
 *     company_name, organization_id, …
 *   }
 *
 * Call resetSubscriptionStatusCache() after the user completes a paid
 * purchase so the cached `trial_expired_at` clears immediately and the
 * navbar pill / guard release the read-only mode on the next render.
 */
import { useCallback, useEffect, useState } from "react";
import { fetchData } from "../api/apiService";
import { SUBSCRIPTIONS } from "../api/endpointMap";

let _statusPromise = null;
let _statusCache = null;

function _unwrap(raw) {
  return raw?.result || raw?.data || raw || {};
}

function _load(force = false) {
  if (force) {
    _statusPromise = null;
    _statusCache = null;
  }
  if (_statusPromise) return _statusPromise;
  _statusPromise = fetchData(SUBSCRIPTIONS.STATUS)
    .then((raw) => {
      _statusCache = _unwrap(raw);
      return _statusCache;
    })
    .catch((err) => {
      _statusPromise = null;
      throw err;
    });
  return _statusPromise;
}

export function resetSubscriptionStatusCache() {
  _statusPromise = null;
  _statusCache = null;
}

export function useSubscriptionStatus() {
  const [state, setState] = useState({
    status: _statusCache,
    isLoading: !_statusCache,
    error: null,
  });

  const refetch = useCallback(() => {
    setState((s) => ({ ...s, isLoading: true, error: null }));
    _load(true)
      .then((status) => setState({ status, isLoading: false, error: null }))
      .catch((err) =>
        setState((s) => ({
          ...s,
          isLoading: false,
          error:
            err?.detail || err?.message || "Failed to load subscription status",
        })),
      );
  }, []);

  useEffect(() => {
    let cancelled = false;
    _load()
      .then((status) => {
        if (!cancelled) setState({ status, isLoading: false, error: null });
      })
      .catch((err) => {
        if (!cancelled) {
          setState((s) => ({
            ...s,
            isLoading: false,
            error:
              err?.detail || err?.message || "Failed to load subscription status",
          }));
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { ...state, refetch };
}
